/**
 * Installation guide catalogue.
 *
 * Artome publishes installation instructions on artome.fi; this app links to
 * them rather than bundling its own copy.
 *
 * This is the ONLY file that changes when a guide is revised or a product ships.
 *
 * `guideUrl` is a stable support-portal permalink and is always the primary
 * action. `pdf.url` is version-stamped (it embeds both an upload month and a
 * version number), so it rots on every revision — which is why it is optional
 * and only ever a secondary action.
 */
import type { ProductKey } from "../lib/serial";

export type GuideKey = "s-series" | "m10" | "x10" | "x20" | "x12";

export interface InstallationGuide {
  key: GuideKey;
  title: string;
  subtitle: string;
  /** Products this guide covers, as returned by `parseArtomeSerial`. */
  products: readonly ProductKey[];
  /**
   * Stable permalink — the primary action.
   *
   * Optional so a product can be a known category before Artome publishes its
   * guide. An entry without one renders as "coming soon" rather than a dead link.
   */
  guideUrl?: string;
  /** Version-stamped direct download — secondary, and optional by design. */
  pdf?: { url: string; version: string; sizeLabel: string };
  /** Only set where the step count has been verified against the document. */
  steps?: number;
}

export const SUPPORT_PORTAL_URL = "https://artome.fi/support/";
export const GUIDES_INDEX_URL =
  "https://artome.fi/support/category/installation-guides/";

export const INSTALLATION_GUIDES: readonly InstallationGuide[] = [
  {
    key: "s-series",
    title: "S-series",
    subtitle: "Projector installation",
    products: ["S1", "S2"],
    guideUrl: "https://artome.fi/support-docs/artome-s1-s2-installation-guide/",
    pdf: {
      url: "https://artome.fi/wp-content/uploads/2026/05/Artome_S_Series_installation_guide_2.0.pdf",
      version: "2.0",
      sizeLabel: "1.2 MB",
    },
    steps: 18,
  },
  {
    key: "m10",
    title: "M10",
    subtitle: "Projector installation",
    products: ["M10"],
    guideUrl: "https://artome.fi/support-docs/artome-m10-installation-guide/",
    pdf: {
      url: "https://artome.fi/wp-content/uploads/2026/06/Artome_M10_projector_installation_guide_2.0.pdf",
      version: "2.0",
      sizeLabel: "1.3 MB",
    },
  },
  {
    // Displayed as X10 (the product line). The document itself is titled
    // "X10M Projector Installation guide" on artome.fi, which is why the URLs
    // below keep x10m in their paths — do not rewrite them to match the title.
    key: "x10",
    title: "X10",
    subtitle: "Projector installation",
    products: ["X10"],
    guideUrl: "https://artome.fi/support-docs/artome-x10m-installation-guide/",
    pdf: {
      url: "https://artome.fi/wp-content/uploads/2026/06/Artome_X10M_installation_guide_3.0.pdf",
      version: "3.0",
      sizeLabel: "1.6 MB",
    },
    steps: 11,
  },
  {
    // Artome publishes an X12 product card but no installation guide yet. The
    // category is listed so installers can see the product is recognised;
    // registration already works for X12 serials.
    key: "x12",
    title: "X12",
    subtitle: "Projector installation",
    products: ["X12"],
  },
  {
    key: "x20",
    title: "X20",
    subtitle: "Projector installation",
    products: ["X20"],
    guideUrl: "https://artome.fi/support-docs/artome-x20-installation-guide/",
    pdf: {
      url: "https://artome.fi/wp-content/uploads/2026/06/Artome_X20_projector_installation_guide_1.0.pdf",
      version: "1.0",
      sizeLabel: "1.5 MB",
    },
  },
];

const BY_PRODUCT = new Map<string, InstallationGuide>();
INSTALLATION_GUIDES.forEach((guide) =>
  guide.products.forEach((product) => BY_PRODUCT.set(product, guide))
);

/**
 * Catalogue entry for a product, or undefined when the product is unknown.
 *
 * A returned entry may still have no `guideUrl` (X12 today), so callers must
 * check for a published guide rather than assuming one — `<GuideLink>` is the
 * single component that does, which keeps the fallback from being forgotten.
 */
export const getGuideForProduct = (
  product: string | undefined
): InstallationGuide | undefined =>
  product ? BY_PRODUCT.get(product.trim().toUpperCase()) : undefined;

export const getGuideByKey = (key: string): InstallationGuide | undefined =>
  INSTALLATION_GUIDES.find((guide) => guide.key === key);

/** True when this product has an installation guide published on artome.fi. */
export const hasPublishedGuide = (
  guide: InstallationGuide | undefined
): guide is InstallationGuide & { guideUrl: string } =>
  Boolean(guide?.guideUrl);
